// State and types of the various Websockets.

// Because first the main websocket is opened, and the waterdepths websocket only
// after the simulation has started, we keep track of which one has already been
// opened here.

// For more documentation of the Websocket functionality,
// see also the middleware, (../reduxMiddleware/WebsocketMiddleware)
// as that module and this one are related a lot. The idea is that the
// middleware imports from here, but not vice versa.

import {
  connect as connectReduxWebsocket,
  disconnect as disconnectReduxWebsocket,
  send as sendReduxWebsocket,
} from "@giantmachines/redux-websocket";

import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppDispatch, AppState, Thunk, appDispatch } from "../App";
import { Asset } from "../types/assets";

import { Line } from "./mapProfileLine";
import { GridCell } from "./simulation";

export enum Websocket {
  Main = "MAIN",
  Waterdepths = "WATERDEPTHS",
  WaterlevelChart = "WATERLEVELCHART",
  WaterFlowChart = "WATERFLOWCHART",
  WaterlevelChartPoint = "WATERLEVELCHARTPOINT",
  GroundWaterChartPoint = "GROUNDWATERCHARTPOINT",
  WaterlevelProfileChart = "WATERLEVELPROFILECHART",
  PumpDischargeChart = "PUMPDISCHARGECHART",
  ActiveSimulations = "ACTIVESIMULATIONS",
  BreachChart = "BREACHCHART",
  RainGraphOnPointChart = "RAINGRAPHONPOINTCHART",
}

const ALL_WEBSOCKET_PREFIXES: string[] = [
  Websocket.Main,
  Websocket.Waterdepths,
  Websocket.WaterlevelChart,
  Websocket.WaterFlowChart,
  Websocket.WaterlevelChartPoint,
  Websocket.GroundWaterChartPoint,
  Websocket.WaterlevelProfileChart,
  Websocket.PumpDischargeChart,
  Websocket.ActiveSimulations,
  Websocket.BreachChart,
  Websocket.RainGraphOnPointChart,
];

// If this function returns true then Typescript knows the type of the string is
// Websocket
export const isWebsocketPrefix = (prefix: string): prefix is Websocket => {
  return ALL_WEBSOCKET_PREFIXES.includes(prefix);
};

// Redux state types and values

export enum WebsocketStatus {
  Open = "OPEN",
  Opening = "OPENING",
  Closed = "CLOSED",
}

export interface WebsocketState {
  status: WebsocketStatus;
}

const INITIAL_STATE: { [key in Websocket]: WebsocketState } = {
  [Websocket.Main]: { status: WebsocketStatus.Closed },
  [Websocket.Waterdepths]: { status: WebsocketStatus.Closed },
  [Websocket.WaterlevelChart]: { status: WebsocketStatus.Closed },
  [Websocket.WaterFlowChart]: { status: WebsocketStatus.Closed },
  [Websocket.WaterlevelChartPoint]: { status: WebsocketStatus.Closed },
  [Websocket.GroundWaterChartPoint]: { status: WebsocketStatus.Closed },
  [Websocket.WaterlevelProfileChart]: { status: WebsocketStatus.Closed },
  [Websocket.PumpDischargeChart]: { status: WebsocketStatus.Closed },
  [Websocket.ActiveSimulations]: { status: WebsocketStatus.Closed },
  [Websocket.BreachChart]: { status: WebsocketStatus.Closed },
  [Websocket.RainGraphOnPointChart]: { status: WebsocketStatus.Closed },
};

// Reducers

export const getWebsockets = (state: AppState) => state.websockets;

// Actions

const WEBSOCKETS_STATUS_UPDATE = "WEBSOCKETS_STATUS_UPDATE";

interface WebsocketsStatusUpdateAction {
  type: typeof WEBSOCKETS_STATUS_UPDATE;
  websocket: Websocket;
  status: WebsocketStatus;
}

export const setWebsocketStatus = (websocket: Websocket, status: WebsocketStatus) => {
  return {
    type: WEBSOCKETS_STATUS_UPDATE,
    websocket,
    status,
  };
};

// Reducer

export default function reducer(state = INITIAL_STATE, action: WebsocketsStatusUpdateAction) {
  switch (action.type) {
    case WEBSOCKETS_STATUS_UPDATE:
      return {
        ...state,
        [action.websocket]: {
          status: action.status,
        },
      };
    default:
      return state;
  }
}

// Helper functions, mostly to be able to use Typescript

export const socketSend = (data: object, prefix: Websocket) => {
  return sendReduxWebsocket(data, prefix);
};

export const socketConnect = (url: string, prefix: Websocket) => {
  return connectReduxWebsocket(url, prefix);
};

export const socketDisconnect = (prefix: Websocket) => {
  return disconnectReduxWebsocket(prefix);
};

// Helper function to close websockets
export const closeWebsocket =
  (websocketType: Websocket): Thunk =>
  (_, getState) => {
    if (getState().websockets[websocketType].status === WebsocketStatus.Open) {
      console.log("Close websocket: " + websocketType);
      appDispatch(socketDisconnect(websocketType));
    }
  };

export const closeAllWebsockets = (): Thunk => (dispatch) => {
  console.log("Closing all active websocket connections");
  dispatch(closeWebsocket(Websocket.Main));
  dispatch(closeWebsocket(Websocket.Waterdepths));
  dispatch(closeWebsocket(Websocket.WaterFlowChart));
  dispatch(closeWebsocket(Websocket.WaterlevelChart));
  dispatch(closeWebsocket(Websocket.WaterlevelProfileChart));
  dispatch(closeWebsocket(Websocket.WaterlevelChartPoint));
  dispatch(closeWebsocket(Websocket.GroundWaterChartPoint));
  dispatch(closeWebsocket(Websocket.PumpDischargeChart));
  dispatch(closeWebsocket(Websocket.BreachChart));
  dispatch(closeWebsocket(Websocket.RainGraphOnPointChart));
};

export const closeAllAssetWebsockets = (dispatch: AppDispatch) => {
  dispatch(closeWebsocket(Websocket.WaterFlowChart));
  dispatch(closeWebsocket(Websocket.WaterlevelChart));
  dispatch(closeWebsocket(Websocket.WaterlevelChartPoint));
  dispatch(closeWebsocket(Websocket.GroundWaterChartPoint));
  dispatch(closeWebsocket(Websocket.PumpDischargeChart));
  dispatch(closeWebsocket(Websocket.BreachChart));
  dispatch(closeWebsocket(Websocket.RainGraphOnPointChart));
};

export const openRainGraphOnPointWebsocket =
  (asset: Asset | GridCell, websocketType: Websocket): Thunk =>
  async (dispatch, getState) => {
    const request: api.WaterLevelGraphRequest = {
      startTime: 0, // Show charts from start of simulation
      historyPointsLimit: 200, // Ideally number of pixels of the chart
      subscribe: true,
      subscribeRateLimit: 1, // Values per second
      nodeId: asset.properties.id,
    };

    // Close other active websockets of previous selected point/asset if they are still active
    closeAllAssetWebsockets(dispatch);

    const v3Api = getV3Api();
    const simulationForRainGraph = getState().simulation.simulation;
    if (simulationForRainGraph === null) return;
    const simulationForRainGraphId = simulationForRainGraph.id;
    if (simulationForRainGraphId === undefined) return;

    try {
      const { url } = await v3Api.simulationsVisualisationsRainGraphCreate({
        simulationPk: simulationForRainGraphId,
        data: request,
      });

      appDispatch(socketConnect(url, websocketType));
    } catch (error) {
      console.error("Caught error wait for water level graph:", error);
      return;
    }
  };

export const openWaterlevelWebsocket =
  (asset: Asset | GridCell, websocketType: Websocket): Thunk =>
  async (dispatch, getState) => {
    const request: api.WaterLevelGraphRequest = {
      startTime: 0, // Show charts from start of simulation
      historyPointsLimit: 200, // Ideally number of pixels of the chart
      subscribe: true,
      subscribeRateLimit: 1, // Values per second
      nodeId: asset.properties.id,
    };

    // Close other active websockets of previous selected point/asset if they are still active
    closeAllAssetWebsockets(dispatch);

    const v3Api = getV3Api();
    const simulationForWaterLevelGraph = getState().simulation.simulation;
    if (simulationForWaterLevelGraph === null) return;
    const simulationForWaterLevelGraphId = simulationForWaterLevelGraph.id;
    if (simulationForWaterLevelGraphId === undefined) return;

    try {
      const { url } = await v3Api.simulationsVisualisationsWaterLevelGraphCreate({
        simulationPk: simulationForWaterLevelGraphId,
        data: request,
      });

      appDispatch(socketConnect(url, websocketType));
    } catch (error) {
      console.error("Caught error wait for water level graph:", error);
      return;
    }
  };

export const openBreachWebsocket =
  (asset: Asset): Thunk =>
  async (dispatch, getState) => {
    // Close other active websockets of previous selected point/asset if they are still active
    closeAllAssetWebsockets(dispatch);

    const v3Api = getV3Api();
    const simulation = getState().simulation.simulation;
    if (simulation === null) return;
    const simulationId = simulation.id;
    if (simulationId === undefined) return;
    const breachThreedimodelId = simulation.threedimodelId;
    if (breachThreedimodelId === undefined) return;

    const potentialBreachesResponse = await v3Api.threedimodelsPotentialbreachesList({
      threedimodelPk: breachThreedimodelId,
      connectedPntId: asset.properties.content_pk,
    });
    const potentialBreaches = potentialBreachesResponse.results;
    const potentialBreach = potentialBreaches[0];

    const request: api.BreachGraphRequest = {
      startTime: 0, // Show charts from start of simulation
      historyPointsLimit: 200, // Ideally number of pixels of the chart
      subscribe: true,
      subscribeRateLimit: 1, // Values per second
      lineId: potentialBreach.lineId,
    };

    try {
      const { url } = await v3Api.simulationsVisualisationsBreachGraphCreate({
        simulationPk: simulationId,
        data: request,
      });
      appDispatch(socketConnect(url, Websocket.BreachChart));
    } catch (error) {
      console.error("Caught error wait for water level graph:", error);
      return;
    }
  };

export const openWaterFlowWebsocket =
  (asset: Asset): Thunk =>
  async (dispatch, getState) => {
    const request: api.WaterFlowGraphRequest = {
      startTime: 0, // Show charts from start of simulation
      historyPointsLimit: 200, // Ideally number of pixels of the chart
      subscribe: true,
      subscribeRateLimit: 1, // Values per second
      lineId: asset.properties.id,
    };

    // Close other active websockets of previous selected point/asset if they are still active
    closeAllAssetWebsockets(dispatch);

    const v3Api = getV3Api();
    const simulationForWaterFlowGraph = getState().simulation.simulation;
    if (simulationForWaterFlowGraph === null) return;
    const simulationForWaterFlowGraphId = simulationForWaterFlowGraph.id;
    if (simulationForWaterFlowGraphId === undefined) return;

    try {
      const { url } = await v3Api.simulationsVisualisationsWaterFlowGraphCreate({
        simulationPk: simulationForWaterFlowGraphId,
        data: request,
      });

      appDispatch(socketConnect(url, Websocket.WaterFlowChart));
    } catch (error) {
      console.error("Caught error wait for water flow graph:", error);
      return;
    }
  };

export const openPumpDischargeWebsocket =
  (asset: Asset): Thunk =>
  async (dispatch, getState) => {
    const request: api.PumpDischargeGraphRequest = {
      startTime: 0, // Show charts from start of simulation
      historyPointsLimit: 200, // Ideally number of pixels of the chart
      subscribe: true,
      subscribeRateLimit: 1, // Values per second
      pumpId: asset.properties.id,
    };

    // Close other active websockets of previous selected point/asset if they are still active
    closeAllAssetWebsockets(dispatch);

    const v3Api = getV3Api();
    const simulationForPumpDischarge = getState().simulation.simulation;
    if (simulationForPumpDischarge === null) return;
    const simulationForPumpDischargeId = simulationForPumpDischarge.id;
    if (simulationForPumpDischargeId === undefined) return;

    try {
      const { url } = await v3Api.simulationsVisualisationsPumpDischargeGraphCreate({
        simulationPk: simulationForPumpDischargeId,
        data: request,
      });

      appDispatch(socketConnect(url, Websocket.PumpDischargeChart));
    } catch (error) {
      console.error("Caught error wait for pump discharge graph:", error);
      return;
    }
  };

export const openWaterlevelProfileWebsocket =
  (line: Line): Thunk =>
  async (dispatch, getState) => {
    const request: api.WaterLevelProfileRequest = {
      geometry: {
        type: "LineString",
        coordinates: line,
      },
      subscribe: true,
      // let pointsLimit be an odd number
      // namely if groundwater is also present then there are  arrays combined. in that case we know it, because the double array will be an even number
      pointsLimit: 61,
      // below variable can request the frequency of the animationframes from backend. Default if not provided is 0.5
      // subscribeRateLimit: 0.25,
    };

    dispatch(closeWebsocket(Websocket.WaterlevelProfileChart));

    const v3Api = getV3Api();
    const simulationForWaterLevelProfile = getState().simulation.simulation;
    if (simulationForWaterLevelProfile === null) return;
    const simulationForWaterLevelProfileId = simulationForWaterLevelProfile.id;
    if (simulationForWaterLevelProfileId === undefined) return;

    try {
      const { url } = await v3Api.simulationsVisualisationsWaterLevelProfileCreate({
        simulationPk: simulationForWaterLevelProfileId,
        data: request,
      });
      console.log("Received url:", url);

      appDispatch(socketConnect(url, Websocket.WaterlevelProfileChart));
    } catch (error) {
      console.error("Caught error wait for water level graph:", error);
      return;
    }
  };
